import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { getService } from "../reducers/service";
import { notify } from "../reducers/NotifierReducer";
import PATAddButton from "./Buttons/PATAddButton";
import PATEditButton from "./Buttons/PATEditButton";
import PATDeleteButton from "./Buttons/PATDeleteButton";
import PATCloseButton from "./Buttons/PATCloseButton";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import DropdownController from "./inputs/DropdownController";
import TextFieldController from "./inputs/TextFieldController";
import PATSaveButton from "./Buttons/PATSaveButton";
import { mapDictionaryOptions, maxLengthMessage } from "../utils/functions";
import { serviceNames } from "../utils/constants";
import {
  addDictionaryValue,
  updateDictionaryValue,
} from "../reducers/DictionaryReducer";

const citySchema = yup.object().shape({
  cityDescription: yup
    .string()
    .max(100, maxLengthMessage(100))
    .required("City is required")
    .nonNullable(),
  state: yup
    .string()
    .max(10, maxLengthMessage(10))
    .required("State is required")
    .nonNullable(),
  category: yup.string().max(10, maxLengthMessage(10)).nullable(),
  isDefault: yup.boolean(),
  isActive: yup.boolean(),
  isDeleted: yup.boolean(),
});

const CityDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [filterCity, setFilterCity] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [defaultState, setDefaultState] = useState("");
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const stateOptions = mapDictionaryOptions(dictionary.codeStates);
  const service = getService(serviceNames.codeCity);

  const city = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      ...g,
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);
  useEffect(() => {
    if (stateOptions && stateOptions.length > 0) {
      const defaultItem = stateOptions.find((item) => item.isDefault === true);
      if (defaultItem) {
        setDefaultState(defaultItem.code);
      }
    }
  }, [stateOptions]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(citySchema),
    defaultValues: {
      cityDescription: "",
      state: "",
      category: "",
      isDefault: false,
      isActive: true,
      isDeleted: false,
    },
  });

  const handleAddClick = () => {
    reset({
      cityDescription: "",
      state: defaultState || "",
      category: "",
      isDefault: false,
      isActive: true,
      isDeleted: false,
    });

    setEditMode(false);
    setShowForm(true);
  };

  const handleEditClick = () => {
    reset({
      cityDescription: selectedRow.CityDescription || "",
      state: selectedRow.State || "",
      category: selectedRow.Category || "",
      isDefault: selectedRow.IsDefault,
      isActive: selectedRow.IsActive,
      isDeleted: selectedRow.IsDeleted,
    });

    setEditMode(true);
    setShowForm(true);
  };

  const filteredCities = useMemo(() => {
    const lower = filterCity.toLowerCase();
    return city.filter(
      (c) =>
        (c.CityDescription || "").toLowerCase().includes(lower) ||
        (c.State || "").toLowerCase().includes(lower) ||
        (c.Category || "").toLowerCase().includes(lower)
    );
  }, [filterCity, city]);

  const columns = useMemo(
    () => [
      { field: "CityDescription", headerName: "City" },
      { field: "State", headerName: "State" },
      { field: "Category", headerName: "Category" },
      {
        field: "IsDefault",
        headerName: "Is Default",
        renderCell: (params) =>
          params.value ? (
            <CheckIcon fontSize="small" />
          ) : (
            <CloseIcon fontSize="small" />
          ),
      },
      {
        field: "IsActive",
        headerName: "Is Active",
        flex: 0.5,
        renderCell: (params) =>
          params.value ? (
            <CheckIcon fontSize="small" />
          ) : (
            <CloseIcon fontSize="small" />
          ),
      },
      {
        field: "IsDeleted",
        headerName: "Is Deleted",
        flex: 0.5,
        renderCell: (params) =>
          params.value ? (
            <CheckIcon fontSize="small" />
          ) : (
            <CloseIcon fontSize="small" />
          ),
      },
    ],
    []
  );

  const confirmDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      await service.patch(selectedRow.ptsCityID, { IsDeleted: true });
      dispatch(notify("City deleted successfully", "success"));
      setSelectedRow(null);
      setOpenDeleteDialog(false);
    } catch (err) {
      dispatch(notify("Failed to delete city", "error"));
    }
  };

  const onSubmit = async (data) => {
    const cleanedData = {
      CityDescription: data.cityDescription.trim() || "",
      State: data.state.trim() || "",
      Category: data.category?.trim() || "",
      IsDefault: data.isDefault || false,
      IsActive: data.isActive || false,
      IsDeleted: data.isDeleted || false,
    };

    try {
      if (editMode) {
        const res = await service.patch(selectedRow.ptsCityID, cleanedData);
        const updatedCities = dictionary.codeCities.map((city) => {
          if (city.ptsCityID === res.ptsCityID) {
            return res;
          } else {
            return city;
          }
        });
        dispatch(updateDictionaryValue(updatedCities, "codeCities"));
        dispatch(notify("City updated successfully", "success"));
        setSelectedRow(null);
      } else {
        const addRes = await service.create(cleanedData);
        dispatch(addDictionaryValue(addRes, "codeCities"));
        dispatch(notify("City added successfully", "success"));
      }

      setShowForm(false);
      setSelectedRow(null);
    } catch (err) {
      dispatch(notify("Failed to save city", "error"));
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedRow(null);
    setShowForm(false);
    reset();
  };

  const handleAddFormClose = () => {
    reset();
    setSelectedRow(null);
    setShowForm(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>CITY</DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {showForm ? (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextFieldController
                    name="cityDescription"
                    label="City"
                    control={control}
                    errors={errors}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DropdownController
                    name="state"
                    control={control}
                    label="State"
                    errors={errors}
                    handleAddClick={handleAddClick}
                    options={stateOptions}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextFieldController
                    name="category"
                    label="Category"
                    control={control}
                    errors={errors}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="isDefault"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={!editMode}
                            />
                          )}
                        />
                      }
                      label="Is Default"
                    />
                    <FormControlLabel
                      control={
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <Checkbox {...field} checked={field.value} />
                          )}
                        />
                      }
                      label="Is Active"
                    />
                    <FormControlLabel
                      control={
                        <Controller
                          name="isDeleted"
                          control={control}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={!editMode}
                            />
                          )}
                        />
                      }
                      label="Is Deleted"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <PATSaveButton onClick={handleSubmit(onSubmit)} />
                    <PATCloseButton onClick={handleAddFormClose} />
                  </Box>
                </Grid>
              </Grid>
            </form>
          ) : (
            <>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quick filter"
                    type="search"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={filterCity}
                    onChange={(e) => setFilterCity(e.target.value)}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent={isSmall ? "center" : "flex-end"}
                  gap={1}
                >
                  <PATAddButton onClick={handleAddClick} />
                  <PATEditButton
                    onClick={handleEditClick}
                    disabled={!selectedRow}
                  />
                  <PATDeleteButton
                    onClick={confirmDelete}
                    disabled={!selectedRow}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Box height={300}>
            <DataGridPro
              rows={filteredCities}
              columns={columns}
              getRowId={(row) => row.ptsCityID}
              hideFooterPagination
              disableColumnMenu
              checkboxSelection={false}
              disableRowSelectionOnClick
              disableColumnFilter
              sx={{
                color: theme.palette.colorFont,
                mt: isSmall ? 1 : 2,
              }}
              onRowClick={(params) => setSelectedRow(params.row)}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <PATCloseButton onClick={handleClose} />
        </DialogActions>
      </Dialog>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default CityDialog;
